import { html, css, LitElement, PropertyValues } from "lit";
import debounce from "lodash-es/debounce";
import { property } from "lit/decorators/property.js";
import { queryAll } from "lit/decorators/query-all.js";
import { state } from "lit/decorators/state.js";
import { progressBarConfigDefaults } from "../../shared/components/progress-bar";
import { shareButtonConfigDefaults } from "../../shared/components/share-button";
import { infoPillLabelConfigDefaults } from "../../shared/components/info-pill";
import { DEFAULT_BASE_URL, SUPPORTED_LANGUAGES } from "../../api-sdk/constants";
import { _errorMessage } from "../../shared/fragments/error-message-template";
import { AsyncController } from "../../shared/controllers/async-controller";
import { _cssVariableMap } from "../../shared/fragments/css-variable-map";
import { defineCustomElement } from "../../shared/defineCustomElement";
import { syncElementHeights } from "../../shared/sync-element-heights";
import { _loading } from "../../shared/fragments/loading-template";
import { makeApiKeyHeader } from "../../utils/makeApiKeyHeader";
import { defineCustomText, useCustomText } from "../../shared/fragments/custom-text";
import { enforceConfig } from "../../shared/enforce-config";
import { TId, TNumericId, TUrl } from "../../shared/types";
import { MissingConfig } from "../../utils/beam-errors";
import { cssResponsiveUtils } from "../../shared/responsive";
import { localizeUserString } from "../../shared/localize";
import { LANGUAGES } from "../../api-sdk/types";
import { getImpact } from "../../api-sdk/v3/routes";
import { cssReset } from "../../shared/css-reset";
import { logger } from "../../utils/logger";
import { cssCardGrid } from "../../shared/fragments/css-card-grid";
import { strings } from "./strings";
import "../../shared/components/progress-bar";
import "../../shared/components/share-button";
import "../../shared/components/info-pill";

type RequiredConfig = {
  apiKey: string;
  nonprofitId: TNumericId;
  beamUserId: TId;
};

export class BeamSubscriptionImpact extends LitElement {
  static tagName = "beam-subscription-impact";

  @property({ type: String, reflect: true }) public baseUrl: TUrl = DEFAULT_BASE_URL;
  @property({ type: String, reflect: false }) apiKey?: RequiredConfig["apiKey"];
  @property({ type: Number }) storeId?: number;
  @property({ type: String, reflect: true }) nonprofitId?: RequiredConfig["nonprofitId"];
  @property({ type: String, reflect: true }) beamUserId?: RequiredConfig["beamUserId"];
  @property({ type: String }) public lang: LANGUAGES = "en";
  @property({ type: Boolean }) public debug = false;
  @property({ type: Boolean }) public draftConfig = false;

  @state() private isMobile = window.innerWidth < 720;

  get configLang() {
    return SUPPORTED_LANGUAGES[this.lang] || "en";
  }

  getImpactData = async () => {
    if (!enforceConfig<RequiredConfig>(["apiKey", "beamUserId", "nonprofitId"], this)) throw new MissingConfig();
    const res = await getImpact({
      baseUrl: this.baseUrl,
      headers: makeApiKeyHeader(this.apiKey),
      queryParams: {
        nonprofitId: this.nonprofitId,
        beamUserId: this.beamUserId,
        personalImpactLimit: 1,
        communityImpactLimit: 1,
        storeId: this.storeId,
        widgetName: "subscription-impact",
        draftConfig: this.draftConfig,
        version: "1.0.0",
        lang: this.configLang,
      },
    });

    return res;
  };

  private impactDataController = new AsyncController<typeof this.getImpactData>(this, this.getImpactData);

  @queryAll(".card-title")
  impactTitleRefs!: HTMLElement[];

  @queryAll(".impact-link")
  impactLinkRefs!: HTMLElement[];

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", this.evaluateBreakPoints);
  }

  async updated(changedProperties: PropertyValues): Promise<void> {
    // Fires on first load as props go from undefined => value
    const requireNewDataProps = ["baseUrl", "storeId", "apiKey", "beamUserId", "lang"];
    for (const prop of requireNewDataProps) {
      if (changedProperties.has(prop)) {
        await this.impactDataController.exec();
        break;
      }
    }
    this.resizeElements(); // Handle layout shifts immediately
    await this.handleFontLoad(this.resizeElements); // If content change triggered font load, resize again
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.resizeElements);
    document.fonts.removeEventListener("loadingdone", this.resizeElements);
    super.disconnectedCallback();
  }

  /**
   * Safari does not fire loading events on the document FontFaceSet, but it does implement the .ready Promise
   * Call this method to wait for font loads that may be triggered by content changes
   */
  async handleFontLoad<T = any>(callback: () => T | void) {
    if (document.fonts.status === "loading") {
      await document.fonts.ready.catch((e) => logger.error(e));
      return callback();
    }
  }

  public get cssVariables() {
    const defaults = {
      ...progressBarConfigDefaults,
      ...shareButtonConfigDefaults,
      ...infoPillLabelConfigDefaults,
      "--beam-fontFamily": "inherit",
      "--beam-fontStyle": "inherit",
      "--beam-fontSize": "12px",
      "--beam-textColor": "inherit",
      "--beam-backgroundColor": "inherit",
      "--beam-subscriptionImpact-impactCards-maxWidth": "900px",
      "--beam-subscriptionImpact-headerContainer-desktop-textAlign": "left",
      "--beam-subscriptionImpact-headerContainer-mobile-textAlign": "center",
      "--beam-subscriptionImpact-descriptionContainer-marginTop": "10px",
      "--beam-subscriptionImpact-cardContainer-marginTop": "15px",
      "--beam-subscriptionImpact-cardContainer-gap": "20px",
      "--beam-subscriptionImpact-card-borderRadius": "8px",
      "--beam-subscriptionImpact-card-background": "#f8fbfb",
      "--beam-subscriptionImpact-card-padding": "24px 28px 24px 24px",
      "--beam-subscriptionImpact-card-marginRight": "0px",
      "--beam-subscriptionImpact-card-marginTop": "10px",
      "--beam-subscriptionImpact-card-gap": "5px",
      "--beam-subscriptionImpact-cardTitle-textAlign": "left",
      "--beam-subscriptionImpact-cardTitle-letterSpacing": "0.36px",
      "--beam-SubscriptionImpact-cardStyle": "'selected_icon'",
      "--beam-SubscriptionImpact-imagePosition": "'inline'",
      "--beam-subscriptionImpact-imageBlock-height": "120px",
      "--beam-subscriptionImpact-imageBlock-borderRadius": "4px",
      "--beam-subscriptionImpact-iconBackground-width": "50px",
      "--beam-subscriptionImpact-iconBackground-height": "50px",
      "--beam-subscriptionImpact-iconBackground-borderRadius": "50%",
      "--beam-subscriptionImpact-iconBackground-background": "#0a323c",
      "--beam-subscriptionImpact-icon-height": "28px",
      "--beam-subscriptionImpact-icon-margin": "auto",
      "--beam-subscriptionImpact-icon-padding": "10px",
      "--beam-subscriptionImpact-icon-borderRadius": "4px",
      "--beam-subscriptionImpact-image-borderRadius": "4px",
      "--beam-subscriptionImpact-cardDescription-padding": "0px",
      "--beam-subscriptionImpact-card-progressBar-padding": "8px 0px",
      "--beam-subscriptionImpact-card-progressBarLabel-gap": "10px",
      "--beam-subscriptionImpact-card-progressBarLabel-textAlign": "right",
      "--beam-subscriptionImpact-card-progressBarLabel-marginLeft": "8px",
      "--beam-subscriptionImpact-card-linkContainer-textAlign": "left",
      "--beam-subscriptionImpact-card-impactLink-textDecoration": "underline",
      "--beam-subscriptionImpact-footer-textAlign": "center",
      "--beam-subscriptionImpact-footer-marginTop": "10px",
      "--beam-subscriptionImpact-share-button-icon-name": "share-2",
      ...defineCustomText("--beam-subscriptionImpact-title", {
        fontSize: "20px",
        fontWeight: "bold",
        lineHeight: "120%",
        textTransform: "capitalize",
        fontStyle: "normal",
        color: "#0a323c",
      }),
      ...defineCustomText("--beam-subscriptionImpact-descriptionPrefix", {
        fontSize: "14px",
        fontWeight: "800",
        lineHeight: "138%",
        fontStyle: "normal",
        color: "#2f302d",
        marginTop: "9px",
      }),
      ...defineCustomText("--beam-subscriptionImpact-description", {
        fontSize: "14px",
        fontWeight: "400",
        lineHeight: "138%",
        fontStyle: "normal",
        color: "#2f302d",
        marginTop: "9px",
      }),
      ...defineCustomText("--beam-subscriptionImpact-cardTitle", {
        fontSize: "12px",
        fontWeight: "bold",
        lineHeight: "120%",
        fontStyle: "normal",
        textTransform: "uppercase",
      }),
      ...defineCustomText("--beam-subscriptionImpact-cardDescription", {
        fontSize: "12px",
        fontWeight: "400",
        lineHeight: "18.2px",
        fontStyle: "normal",
        color: "#454a47",
      }),
      ...defineCustomText("--beam-subscriptionImpact-card-progressBarLabel", {
        fontSize: "10px",
        color: "#878a92",
      }),
      ...defineCustomText("--beam-subscriptionImpact-card-impactLink", {
        fontSize: "12px",
        fontWeight: "600",
        lineHeight: "16px",
        fontStyle: "normal",
        color: "#454a47",
        marginTop: "auto",
      }),
    };
    const remoteConfig = this.impactDataController?.data?.config?.web?.theme || {};
    const config = {
      ...defaults,
      ...remoteConfig,
    };
    const serializable = Object.create({
      toCSS() {
        return _cssVariableMap(this as Record<string, string>);
      },
    });
    return Object.assign(serializable, config);
  }

  evaluateBreakPoints = debounce(
    () => {
      this.isMobile = window.innerWidth < 768;
    },
    50,
    { maxWait: 50, leading: true }
  );

  resizeElements = debounce(
    () => {
      syncElementHeights(this.impactTitleRefs);
      syncElementHeights(this.impactLinkRefs);
    },
    50,
    { maxWait: 50, leading: true }
  );

  render() {
    const { data, loading, error } = this.impactDataController;
    if (loading) {
      return _loading();
    }
    if (error || data == null) {
      return this.debug ? _errorMessage({ error: error || new Error("No data") }) : "";
    }

    const brandName = data.chain.name;
    const causeName = localizeUserString(this.configLang, data.personal[0].nonprofit.cause || "");
    const nonprofitName = data.personal[0].nonprofit.name;
    const personalProgressPercentage = data.personal[0]?.impact?.goalProgressPercentage || 0;
    const communityProgressPercentage = data.community[0].impact?.goalProgressPercentage || 0;
    const personalProgressDescription = `${personalProgressPercentage}% ${
      localizeUserString(this.configLang, data?.config?.web?.additionalStrings?.["percentFunded"] || "") ||
      strings[this.configLang].percentFunded()
    }`;
    const communityProgressDescription = `${communityProgressPercentage}% ${
      localizeUserString(this.configLang, data?.config?.web?.additionalStrings?.["percentFunded"] || "") ||
      strings[this.configLang].percentFunded()
    }`;
    const communityImage = data.community[0]?.imageUrl || "";
    const personalImage = data.personal[0]?.imageUrl || "";
    const unselectedIcon = data.personal[0].nonprofit.causeIconUrl;
    const selectedIcon = data.personal[0].nonprofit.causeIconSelectedUrl;
    const cardStyle = this.cssVariables["--beam-SubscriptionImpact-cardStyle"];
    const imagePosition = this.cssVariables["--beam-SubscriptionImpact-imagePosition"];
    const isImage = cardStyle === "'image'";
    const iconImage = cardStyle === "'selected_icon'" ? selectedIcon : unselectedIcon;
    const communityCardImage = isImage ? communityImage : iconImage;
    const personalCardImage = isImage ? personalImage : iconImage;
    const personalTitle =
      localizeUserString(this.configLang, data?.config?.web?.dynamicStrings?.["ctaTitle"] || "") ||
      strings[this.configLang].title({ causeName });

    const personalFutureTenseTitle =
      localizeUserString(this.configLang, data?.config?.web?.dynamicStrings?.["ctaFutureTenseTitle"] || "") ||
      strings[this.configLang].futureTenseTitle({ causeName });
    const communityLink = data.store.communityPageURL;
    const personalLink = data.personal[0].nonprofit?.website;
    const shareImage = data.personal[0].share?.imageUrl;
    const shareIcon = this.cssVariables["--beam-SubscriptionImpact-share-button-icon-name"] || "share-2";
    return html`
      <style>
        :host {
          ${this.cssVariables.toCSS()}
        }
      </style>

      <div class="widget-container" id="beam-subscription-impact-container">
        <div
          class="header-container ${this.isMobile ? "header-container-mobile" : "header-container-desktop"}"
          id="beam-subscriptionImpact-header-container"
        >
          <h3 class="title" part="title" id="beam-subscriptionImpact-title">
            ${personalProgressPercentage <= 1 ? personalFutureTenseTitle : personalTitle}
          </h3>
          <div
            class="description-container ${this.isMobile
              ? "header-container-mobile description-mobile"
              : "header-container-desktop description-desktop"}"
          >
            <span class="description-prefix" part="description">
              ${localizeUserString(this.configLang, data?.config?.web?.additionalStrings?.["ctaMessagePrefix"] || "") ||
              strings[this.configLang].ctaMessagePrefix()}
            </span>
            <span class="description" part="description" id="beam-subscriptionImpact-description">
              ${localizeUserString(this.configLang, data?.config?.web?.dynamicStrings?.["ctaMessage"] || "") ||
              strings[this.configLang].description({ nonprofit: nonprofitName })}
            </span>
          </div>
        </div>
        <div class="root">
          <div
            class="card-container cardGrid ${this.isMobile ? "card-container-mobile" : ""}"
            id="beam-subscriptionImpact-card-container"
          >
            ${this.renderInlineCard({
              title:
                personalProgressPercentage <= 1
                  ? localizeUserString(
                      this.configLang,
                      data?.config?.web?.additionalStrings?.["futureTensePersonalImpactTitle"] || ""
                    ) || strings[this.configLang].futureTensePersonalImpactTitle()
                  : localizeUserString(
                      this.configLang,
                      data?.config?.web?.additionalStrings?.["personalImpactTitle"] || ""
                    ) || strings[this.configLang].personalImpactTitle(),
              image: personalCardImage,
              description: localizeUserString(this.configLang, data.personal[0]?.impact?.description || ""),
              link: personalLink,
              linkText:
                localizeUserString(this.configLang, data?.config?.web?.dynamicStrings?.["personalLinkCopy"] || "") ||
                strings[this.configLang].personalLinkCopy({ nonprofit: nonprofitName }),
              progressBarValue: personalProgressPercentage,
              progressBarText: personalProgressDescription,
              infoPill:
                localizeUserString(this.configLang, data?.config?.web?.additionalStrings?.["progressPillText"] || "") ||
                strings[this.configLang].progressPillText(),
              imageType: cardStyle,
              cardType: "personal",
              imagePosition: imagePosition,
            })}
            ${this.renderInlineCard({
              title:
                localizeUserString(
                  this.configLang,
                  data?.config?.web?.additionalStrings?.["communityImpactTitle"] || ""
                ) || strings[this.configLang].communityImpactTitle(),
              image: communityCardImage,
              description: localizeUserString(this.configLang, data.community[0].impact?.description || ""),
              link: communityLink,
              linkText:
                localizeUserString(this.configLang, data?.config?.web?.dynamicStrings?.["communityLinkCopy"] || "") ||
                strings[this.configLang].communityLinkCopy({ brandName: brandName }),
              progressBarValue: communityProgressPercentage,
              progressBarText: communityProgressDescription,
              infoPill:
                localizeUserString(this.configLang, data?.config?.web?.additionalStrings?.["progressPillText"] || "") ||
                strings[this.configLang].progressPillText(),
              imageType: cardStyle,
              cardType: "community",
              imagePosition: imagePosition,
            })}
          </div>
        </div>
        <div class="footer" part="social-share">
          <beam-share-button
            imageUrl="${shareImage}"
            brandName="${brandName}"
            shareIcon="${shareIcon}"
            buttonText="${localizeUserString(
              this.configLang,
              data?.config?.web?.additionalStrings?.["shareButtonText"] || ""
            )}"
            configLang="${this.configLang}"
          ></beam-share-button>
        </div>
      </div>
    `;
  }

  renderInlineCard(impactCard: {
    title: string;
    description: string;
    image: string | null;
    link: string | null;
    linkText: string;
    progressBarValue?: number;
    progressBarText?: string;
    infoPill: string;
    imageType: "'image'" | "'icon'" | "'selected_icon'";
    cardType: "personal" | "community";
    imagePosition: "'inline'" | "'block'";
  }) {
    const isPersonalProgressBarValueLow =
      impactCard.progressBarValue !== undefined &&
      impactCard.progressBarValue <= 1 &&
      impactCard.cardType === "personal";
    return html`
      <div class="inline-card ${this.isMobile ? "" : "inline-card-desktop"}">
        ${impactCard.image && impactCard.imagePosition === "'block'" && impactCard.imageType === "'image'"
          ? html` <div
              class="image-block-container"
              style="background: url('${impactCard.image}');background-position: center;
          background-size: cover;
          background-repeat: no-repeat;"
            ></div>`
          : ""}
        <h4 class="card-title">${impactCard.title}</h4>
        <div class="inline-content">
          ${impactCard.image && impactCard.imagePosition === "'inline'"
            ? html`${impactCard.imageType === "'icon'" || impactCard.imageType === "'selected_icon'"
                ? html` <div class="icon-container">
                    <div class="icon-background">
                      <img src="${impactCard.image}" alt="" role="presentation" class="icon" />
                    </div>
                  </div>`
                : html` <div class="image-container">
                    <img src="${impactCard.image}" alt="" role="presentation" class="image" />
                  </div>`}`
            : ""}
          <div class="impact-container" part="details">
            <p class="card-description">${impactCard.description}</p>
            <div class="card-progress-bar">
              ${isPersonalProgressBarValueLow
                ? html` <beam-info-pill infoText="${impactCard.infoPill}"></beam-info-pill> `
                : html`
                    <beam-progress-bar value="${impactCard.progressBarValue}" style="flex: 1 0;"> </beam-progress-bar>
                    <span class="details-funding-progress-label"> ${impactCard.progressBarText} </span>
                  `}
            </div>
            <div class="link-container">
              <a href="${impactCard.link}" class="impact-link" target="_blank">${impactCard.linkText}</a>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  static styles = [
    cssReset,
    cssResponsiveUtils,
    css`
      :host {
        display: block;
        font-family: var(--beam-fontFamily);
        font-style: var(--beam-fontStyle);
        font-size: var(--beam-fontSize);
        background-color: var(--beam-backgroundColor);
        color: var(--beam-textColor);
        max-width: var(--beam-subscriptionImpact-impactCards-maxWidth);
        word-break: normal;
      }

      /* subscription impact container and header css */

      .header-container-mobile {
        text-align: var(--beam-subscriptionImpact-headerContainer-mobile-textAlign);
      }

      .header-container-desktop {
        text-align: var(--beam-subscriptionImpact-headerContainer-desktop-textAlign);
      }

      .title {
        ${useCustomText("--beam-subscriptionImpact-title")};
      }

      .description-container {
        margin-top: var(--beam-subscriptionImpact-descriptionContainer-marginTop);
      }

      .description-prefix {
        ${useCustomText("--beam-subscriptionImpact-descriptionPrefix")};
      }

      .description {
        ${useCustomText("--beam-subscriptionImpact-description")};
      }

      .root {
        display: flex;
        flex-direction: column;
        gap: 12px;
        flex-shrink: 0;
      }

      .card-container {
        margin-top: var(--beam-subscriptionImpact-cardContainer-marginTop);
        gap: var(--beam-subscriptionImpact-cardContainer-gap);
        display: grid;
        justify-content: center;
      }

      .card-container-mobile {
        flex-direction: column;
      }

      /* subscription impact card css */
      .inline-card {
        border-radius: var(--beam-subscriptionImpact-card-borderRadius);
        background: var(--beam-subscriptionImpact-card-background);
        padding: var(--beam-subscriptionImpact-card-padding);
        flex: 2;
        flex-direction: column;
        margin-top: var(--beam-subscriptionImpact-card-marginTop);
        display: flex;
        gap: var(--beam-subscriptionImpact-card-gap);
      }

      .inline-card-desktop {
        margin-right: var(--beam-subscriptionImpact-card-marginRight);
      }

      .inline-content {
        display: flex;
        flex-direction: row;
        gap: 15px;
        height: 100%;
      }

      .card-title {
        text-align: var(--beam-subscriptionImpact-cardTitle-textAlign);
        letter-spacing: var(--beam-subscriptionImpact-cardTitle-letterSpacing);
        ${useCustomText("--beam-subscriptionImpact-cardTitle")};
      }

      .icon-container {
        flex: 0;
        flex-direction: column;
      }

      .image-block-container {
        flex-shrink: 0;
        height: var(--beam-subscriptionImpact-imageBlock-height);
        border-radius: var(--beam-subscriptionImpact-imageBlock-borderRadius);
      }

      .image-container {
        flex: 4;
        display: flex;
        flex-direction: column;
      }

      .icon-background {
        width: var(--beam-subscriptionImpact-iconBackground-width);
        height: var(--beam-subscriptionImpact-iconBackground-height);
        border-radius: var(--beam-subscriptionImpact-iconBackground-borderRadius);
        padding: var(--beam-subscriptionImpact-icon-padding);
        overflow: hidden;
        background: var(--beam-subscriptionImpact-iconBackground-background);
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .icon {
        height: var(--beam-subscriptionImpact-icon-height);
        display: block;
        margin: var(--beam-subscriptionImpact-icon-margin);
        border-radius: var(--beam-subscriptionImpact-icon-borderRadius);
      }

      .image {
        flex: 4;
        height: auto;
        width: 100%;
        object-fit: cover;
        border-radius: var(--beam-subscriptionImpact-image-borderRadius);
      }

      .impact-container {
        flex: 1 1 67%;
        height: 100%;
        display: flex;
        flex-direction: column;
        flex-wrap: nowrap;
        justify-content: space-between;
        align-content: stretch;
        align-items: stretch;
        position: relative;
      }

      .card-description {
        flex-grow: 1;
        padding: var(--beam-subscriptionImpact-cardDescription-padding);
        ${useCustomText("--beam-subscriptionImpact-cardDescription")};
      }

      .card-progress-bar {
        flex: 1 1;
        display: flex;
        align-items: center;
        padding: var(--beam-subscriptionImpact-card-progressBar-padding);
      }

      .details-funding-progress-label {
        gap: var(--beam-subscriptionImpact-card-progressBarLabel-gap);
        white-space: nowrap;
        text-align: var(--beam-subscriptionImpact-card-progressBarLabel-textAlign);
        flex: 0 1;
        margin-left: var(--beam-subscriptionImpact-card-progressBarLabel-marginLeft);
        ${useCustomText("--beam-subscriptionImpact-card-progressBarLabel")};
      }

      .link-container {
        text-align: var(--beam-subscriptionImpact-card-linkContainer-textAlign);
      }

      .impact-link {
        display: inline-block;
        text-decoration-line: var(--beam-subscriptionImpact-card-impactLink-textDecoration);
        ${useCustomText("--beam-subscriptionImpact-card-impactLink")};
      }

      /* share footer */
      .footer {
        text-align: var(--beam-subscriptionImpact-footer-textAlign);
        margin-top: var(--beam-subscriptionImpact-footer-marginTop);
      }
    `,
    cssCardGrid({
      gap: "var(--beam-subscriptionImpact-cardContainer-gap)",
      itemMinWidth: "250px",
      columnCount: "2",
    }),
  ];
}

defineCustomElement(BeamSubscriptionImpact);

declare global {
  interface HTMLElementTagNameMap {
    "beam-subscription-impact": BeamSubscriptionImpact;
  }
}
